import React from 'react';
import {
    BlankModal,
    Box,
    cssVar,
    Flex,
    HStack,
    InlineBanner,
    Input,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
} from '@cad-arc';
import { useForm, useFieldArray, useWatch, Control } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { StationsResource } from '@mark43/cad-api';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { FormNumberInput } from '../forms/integrated-form-controls/form-number-input';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useApiClient } from '~/api-client';
import { Tooltip } from '../common/components/tooltip';
import {
    availableLabel,
    isOverCommitted,
    matchesFilter,
    buildRows,
    toStationContexts,
    StaffingPoolRow,
} from './fire-station-staffing-pool-rows';

export type FireStationStaffingPoolModalComponentType = typeof FireStationStaffingPoolModal;

const MAX_PERSONNEL_COUNT = 999999;

type StaffingPoolFormData = { rows: StaffingPoolRow[] };

const FireStationStaffingPoolModal = () => {
    const apiClient = useApiClient();
    const modalControls = useFullscreenModalControls();
    const closeModal = React.useCallback(() => modalControls.close(), [modalControls]);

    const stationsQuery = useQuery(['fire-station-staffing-pool'], () =>
        apiClient
            .request<StationsResource.BootstrapStationContext>({
                method: 'POST',
                url: 'stations/context/bootstrap',
                data: {},
            })
            .then((resp) => resp?.data.data)
    );

    const form = useForm<StaffingPoolFormData>({ defaultValues: { rows: [] }, mode: 'onBlur' });
    const { fields } = useFieldArray({ control: form.control, name: 'rows' });

    // Populate the grid once the bootstrap resolves.
    React.useEffect(() => {
        if (stationsQuery.data) {
            form.reset({ rows: buildRows(stationsQuery.data) });
        }
    }, [stationsQuery.data, form]);

    const [query, setQuery] = React.useState('');
    const rowErrors = form.formState.errors.rows;
    // Sticky on each header cell (not the thead) with an opaque background so scrolling rows never show through.
    const stickyHeaderStyle: React.CSSProperties = {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: cssVar('arc.colors.surface.foreground'),
    };

    // Rows are hidden by the filter, never unmounted, so Save validates every station and sends each changed one; a row
    // with a validation error is always shown so the dispatcher can see and fix it.
    const isRowVisible = (row: StaffingPoolRow, index: number) =>
        !!rowErrors?.[index] || matchesFilter(row, query);

    const mutation = useMutation(
        (values: StaffingPoolFormData) => {
            const stationContexts = toStationContexts(values.rows);
            // Nothing changed: there is nothing to write, so close without a request.
            if (stationContexts.length === 0) {
                return Promise.resolve(undefined);
            }
            return apiClient.request<StationsResource.SetStationPersonnelCount>({
                method: 'POST',
                url: 'stations/context',
                data: { stationContexts },
            });
        },
        { onSuccess: () => closeModal() }
    );

    const onSubmit = React.useCallback(
        (values: StaffingPoolFormData) => mutation.mutate(values),
        [mutation]
    );

    return (
        <IntegratedFormProvider
            query={stationsQuery}
            form={form}
            mutation={mutation}
            onSubmit={onSubmit}
        >
            <BlankModal size="xl" scrollBehavior="inside" isOpen={true} onClose={closeModal}>
                <ModalHeader>Fire station staffing</ModalHeader>
                {/* No top padding, so the sticky header sits flush with the top of the scroll area and no row shows above it. */}
                <ModalBody style={{ paddingTop: 0 }}>
                    <FormBody>
                        <Flex direction="column" gap="3" minHeight="0" paddingTop="4">
                            <Text>
                                Enter the total number of personnel currently on duty at each
                                station. Available is calculated from the crews committed to active
                                events and refreshes after saving.
                            </Text>
                            <OverCommitBanner control={form.control} />
                            <Input
                                type="text"
                                leftIcon="Search"
                                isClearable
                                aria-label="Filter stations"
                                placeholder="Filter stations"
                                value={query}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setQuery(e.currentTarget.value)
                                }
                                onKeyDown={(e: React.KeyboardEvent) => {
                                    // Escape clears a non-empty filter; don't let it also close the modal.
                                    if (e.key === 'Escape' && query) {
                                        e.stopPropagation();
                                    }
                                }}
                            />
                            {/* The modal body scrolls (scrollBehavior="inside"); the header row sticks to its top. */}
                            <Box>
                                <Table size="sm" width="100%">
                                    <Thead>
                                        <Tr>
                                            <Th style={stickyHeaderStyle}>Station</Th>
                                            <Th style={stickyHeaderStyle}>Total on duty</Th>
                                            <Th style={stickyHeaderStyle}>Available</Th>
                                            <Th style={stickyHeaderStyle}>Committed</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {fields.map((field, index) => (
                                            <StationRow
                                                key={field.id}
                                                row={field}
                                                index={index}
                                                control={form.control}
                                                hidden={!isRowVisible(field, index)}
                                            />
                                        ))}
                                    </Tbody>
                                </Table>
                            </Box>
                        </Flex>
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <HStack>
                        <FormCancelButton onClick={closeModal} />
                        <FormSaveButton />
                    </HStack>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
};

/**
 * Live over-commit warning: fires when a station's committed crews exceed the total the dispatcher has
 * entered. It only warns and never blocks saving — the entered total is the source of truth, and the
 * committed figures are a snapshot from when the modal opened, so they may already be out of date. Watches the edited totals so it reacts as they type, not just on load.
 * InlineBanner renders its copy via title/description props (children are ignored), so the message must
 * go there — passing it as children renders an empty banner.
 */
export const OverCommitBanner = ({ control }: { control: Control<StaffingPoolFormData> }) => {
    const rows = useWatch({ control, name: 'rows' }) ?? [];
    const overCommitted = rows.filter(isOverCommitted).map((row) => `Station ${row.label}`);
    if (overCommitted.length === 0) {
        return null;
    }
    return (
        <Box marginBottom="4">
            <InlineBanner
                status="error"
                title="Committed personnel (as of open) exceeds the total on duty"
                description={overCommitted.join(', ')}
            />
        </Box>
    );
};

const UNSET_TOTAL_INPUT_PROPS = { placeholder: 'Not set' };

/**
 * One station: label, editable total, then the read-only available and committed figures. A row whose
 * committed crews exceed the entered total is flagged inline, since the banner above may be scrolled away.
 */
const StationRow = ({
    row,
    index,
    control,
    hidden,
}: {
    row: StaffingPoolRow;
    index: number;
    control: Control<StaffingPoolFormData>;
    hidden: boolean;
}) => {
    const total = useWatch({ control, name: `rows.${index}.total` });
    const overCommitted = isOverCommitted({ ...row, total: total ?? row.total });
    return (
        <Tr style={hidden ? { display: 'none' } : undefined}>
            <Td>
                <Text fontWeight="bold">Station {row.label}</Text>
            </Td>
            <Td>
                <FormNumberInput
                    control={control}
                    name={`rows.${index}.total`}
                    aria-label={`Total personnel for station ${row.label}`}
                    numberInputProps={UNSET_TOTAL_INPUT_PROPS}
                    rules={{
                        // A station without a total may stay blank; one that has a total can't be cleared.
                        required: row.hasTotal ? 'Required' : false,
                        min: { value: 0, message: 'Must be at least 0' },
                        max: {
                            value: MAX_PERSONNEL_COUNT,
                            message: `Must be at most ${MAX_PERSONNEL_COUNT}`,
                        },
                    }}
                />
            </Td>
            <Td>
                <Text color="text.placeholder">{availableLabel(row)}</Text>
            </Td>
            <Td>
                {row.computedAvailable != null && row.committedUnits.length > 0 ? (
                    <CommittedUnitsTooltip units={row.committedUnits} committedCount={row.committedCount} />
                ) : (
                    <Text as="span" color="text.placeholder">
                        —
                    </Text>
                )}
                {overCommitted && (
                    <Text as="span" color="negative" style={{ marginLeft: 8 }}>
                        Over-committed
                    </Text>
                )}
            </Td>
        </Tr>
    );
};

/**
 * The committed personnel as a count (the same unit of measure as "Available") with a hover tooltip of each
 * committed unit and the crew it debits — keeps the readout one line no matter how many units are out. The
 * count is plain light-gray text (no decorator, matching the rest of the readout).
 */
export const CommittedUnitsTooltip = ({
    units,
    committedCount,
}: {
    units: StaffingPoolRow['committedUnits'];
    committedCount: number;
}) => (
    <Tooltip overlay={units.map((unit) => `${unit.callSign} (${unit.personnelCount})`).join(' ')}>
        <Text as="span" color="text.placeholder" style={{ cursor: 'default' }}>
            {committedCount}
        </Text>
    </Tooltip>
);

export default FireStationStaffingPoolModal;
